import React from 'react';
import {
  Beaker,
  Users,
  Clock,
  Sparkles,
  TrendingUp,
  Flame,
  Star,
  Globe,
  Eye,
  UserPlus,
  MessageCircle,
  CheckSquare,
  FolderOpen,
  BookOpen,
  Calendar,
  Settings,
  ArrowLeft,
  ChevronRight,
  Circle,
  Sun,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  AlertCircle,
  MoreHorizontal,
  Plus,
  Filter,
  LayoutGrid,
  BarChart3,
  RefreshCw,
  Send,
  Paperclip,
  Smile,
  Image,
  FileText,
  Download,
  Share2,
  Search,
  Video,
  Mic,
  MoreVertical,
  ChevronLeft,
  ChevronDown,
  File,
  FileImage,
  FileCode,
  FilePlus,
  Folder,
  Link,
  ExternalLink,
  Heart,
  MessageSquare,
  Play,
  Pause,
  Volume2,
} from 'lucide-react';
import './LabsMockupContent.css';

const LabsMockupContent = ({ 
  visibleCards = 0, 
  animationPhase,
  showInterior = false,
  visibleTasks = 0,
  activeInteriorTab = 'chat', // 'chat', 'tasks', 'files', 'library', 'calendar'
  onTabClick = () => {}, // Handler for tab clicks
}) => {
  // Research Labs data
  const labsData = [
    {
      id: 1,
      name: 'Philosophy of Mind Lab',
      description: 'Exploring consciousness, ethics, and ancient philosophical frameworks...',
      image: 'philosophy',
      fieldImage: '/field-images/philosophy.jpg',
      tags: ['Philosophy', 'Ethics'],
      match: 89,
      quality: 5,
      leader: { name: 'Dr. Lena Hoffmann', avatar: 'LH' },
      members: 12,
      maxMembers: 15,
      spotsLeft: 3,
      lastActive: '2 hours ago',
      color: 'indigo',
    },
    {
      id: 2,
      name: 'Climate Modeling Lab',
      description: 'Advanced climate models using ML and statistical analysis...',
      image: 'climate',
      fieldImage: '/field-images/environmental-science.jpeg',
      tags: ['Environmental', 'Data Science'],
      match: 76,
      quality: 5,
      leader: { name: 'Prof. Idris Malik', avatar: 'IM' },
      members: 8,
      maxMembers: 10,
      spotsLeft: 2,
      lastActive: '1 day ago',
      color: 'green',
    },
    {
      id: 3,
      name: 'Quantum Computing',
      description: 'Exploring quantum algorithms and their applications...',
      image: 'quantum',
      fieldImage: '/field-images/physics.jpg',
      tags: ['Physics', 'Computing'],
      match: 64,
      quality: 4,
      leader: { name: 'Dr. Farid Anwar', avatar: 'FA' },
      members: 6,
      maxMembers: 8,
      spotsLeft: 2,
      lastActive: '3 hours ago',
      color: 'cyan',
    },
    {
      id: 4,
      name: 'World Geography Lab',
      description: 'Geospatial analysis, cartography, and global environmental mapping...',
      image: 'geography',
      fieldImage: '/field-images/geography.jpg',
      tags: ['Geography', 'Geospatial'],
      match: 92,
      quality: 5,
      leader: { name: 'Dr. Amina Rashid', avatar: 'AR' },
      members: 14,
      maxMembers: 15,
      spotsLeft: 1,
      lastActive: '30 min ago',
      color: 'pink',
    },
    {
      id: 5,
      name: 'Drug Discovery AI',
      description: 'ML-driven pharmaceutical research and molecular design...',
      image: 'pharma',
      fieldImage: '/field-images/chemistry.jpg',
      tags: ['Biology', 'Chemistry'],
      match: 71,
      quality: 4,
      leader: { name: 'Dr. Ingrid Svensson', avatar: 'IS' },
      members: 9,
      maxMembers: 12,
      spotsLeft: 3,
      lastActive: '5 hours ago',
      color: 'amber',
    },
    {
      id: 6,
      name: 'Robotics & Control',
      description: 'Autonomous systems, computer vision, and robot learning...',
      image: 'robotics',
      fieldImage: '/field-images/engineering.jpg',
      tags: ['Robotics', 'Control Systems'],
      match: 58,
      quality: 4,
      leader: { name: 'Prof. Owen Gallagher', avatar: 'OG' },
      members: 7,
      maxMembers: 10,
      spotsLeft: 3,
      lastActive: '1 week ago',
      color: 'blue',
    },
  ];

  // Chat Messages Data
  const chatMessages = [
    {
      id: 1,
      user: { name: 'Lena Hoffmann', avatar: 'LH', role: 'Leader' },
      message: "Hey team! I've uploaded the new RLHF paper review. Please check the Library section.",
      time: '10:32 AM',
      isMe: false,
      reactions: [{ emoji: '👍', count: 3 }, { emoji: '🔥', count: 2 }],
    },
    {
      id: 2,
      user: { name: 'Farid Anwar', avatar: 'FA', role: 'Member' },
      message: "Great work on the baseline implementation! The results look promising.",
      time: '10:45 AM',
      isMe: false,
      reactions: [{ emoji: '🎉', count: 4 }],
    },
    {
      id: 3,
      user: { name: 'You', avatar: 'MA', role: 'Member' },
      message: "Thanks! I'll push the updated code to the repo today. Also found some interesting edge cases we should discuss.",
      time: '10:48 AM',
      isMe: true,
      reactions: [],
    },
    {
      id: 4,
      user: { name: 'Amina Rashid', avatar: 'AR', role: 'Member' },
      message: "Can we schedule a call for tomorrow? Need to align on the methodology section.",
      time: '11:02 AM',
      isMe: false,
      reactions: [{ emoji: '✅', count: 2 }],
    },
    {
      id: 5,
      user: { name: 'Idris Malik', avatar: 'IM', role: 'Member' },
      message: "I've added a new task for the data preprocessing pipeline. @Farid can you take a look?",
      time: '11:15 AM',
      isMe: false,
      hasAttachment: true,
      attachmentName: 'pipeline_spec.pdf',
      reactions: [],
    },
  ];

  // Tasks Kanban Data
  const taskColumns = [
    { id: 'todo', name: 'To Do', icon: Circle, color: 'slate', count: 3 },
    { id: 'progress', name: 'In Progress', icon: Sun, color: 'amber', count: 2 },
    { id: 'review', name: 'Review', icon: Eye, color: 'cyan', count: 1 },
    { id: 'completed', name: 'Completed', icon: CheckCircle2, color: 'green', count: 4 },
    { id: 'blocked', name: 'Blocked', icon: XCircle, color: 'red', count: 1 },
  ];

  const tasks = [
    { id: 1, column: 'todo', title: 'Literature review on RLHF', priority: 'high', assignee: 'SC', dueDate: 'Feb 3' },
    { id: 2, column: 'todo', title: 'Setup experiment environment', priority: 'medium', assignee: 'AK', dueDate: 'Feb 5' },
    { id: 3, column: 'todo', title: 'Draft methodology section', priority: 'low', assignee: 'PS', dueDate: 'Feb 8' },
    { id: 4, column: 'progress', title: 'Implement baseline model', priority: 'high', assignee: 'SC', dueDate: 'Feb 2' },
    { id: 5, column: 'progress', title: 'Data preprocessing pipeline', priority: 'medium', assignee: 'JL', dueDate: 'Feb 4' },
    { id: 6, column: 'review', title: 'Results analysis document', priority: 'high', assignee: 'PS', dueDate: 'Feb 1' },
    { id: 7, column: 'completed', title: 'Project proposal', priority: 'high', assignee: 'SC', dueDate: 'Jan 25' },
    { id: 8, column: 'completed', title: 'Team onboarding', priority: 'medium', assignee: 'AK', dueDate: 'Jan 20' },
    { id: 9, column: 'blocked', title: 'GPU cluster access', priority: 'high', assignee: 'JL', dueDate: 'Jan 28' },
  ];

  // Files Data
  const filesData = {
    folders: [
      { id: 1, name: 'Research Papers', files: 24, updated: '2 hours ago', color: 'blue' },
      { id: 2, name: 'Experiments', files: 18, updated: '1 day ago', color: 'green' },
      { id: 3, name: 'Meeting Notes', files: 12, updated: '3 days ago', color: 'amber' },
    ],
    recentFiles: [
      { id: 1, name: 'RLHF_baseline_results.ipynb', type: 'code', size: '2.4 MB', updated: '30 min ago', author: 'SC' },
      { id: 2, name: 'methodology_v3.docx', type: 'doc', size: '1.2 MB', updated: '2 hours ago', author: 'PS' },
      { id: 3, name: 'training_curves.png', type: 'image', size: '890 KB', updated: '4 hours ago', author: 'AK' },
      { id: 4, name: 'dataset_preprocessing.py', type: 'code', size: '45 KB', updated: '1 day ago', author: 'JL' },
      { id: 5, name: 'literature_review.pdf', type: 'pdf', size: '3.8 MB', updated: '2 days ago', author: 'SC' },
    ],
  };

  // Library Data
  const libraryData = {
    papers: [
      { 
        id: 1, 
        title: 'Constitutional AI: Harmlessness from AI Feedback', 
        authors: 'Bai et al.', 
        year: '2023',
        journal: 'Anthropic',
        citations: 892,
        addedBy: 'SC',
        notes: 3,
        highlighted: true,
      },
      { 
        id: 2, 
        title: 'Training language models to follow instructions with human feedback', 
        authors: 'Ouyang et al.', 
        year: '2022',
        journal: 'NeurIPS',
        citations: 2341,
        addedBy: 'PS',
        notes: 5,
        highlighted: true,
      },
      { 
        id: 3, 
        title: 'Scaling Laws for Reward Model Overoptimization', 
        authors: 'Gao et al.', 
        year: '2023',
        journal: 'ICML',
        citations: 456,
        addedBy: 'AK',
        notes: 2,
        highlighted: false,
      },
      { 
        id: 4, 
        title: 'Direct Preference Optimization: Your Language Model is a Reward Model', 
        authors: 'Rafailov et al.', 
        year: '2023',
        journal: 'NeurIPS',
        citations: 1203,
        addedBy: 'SC',
        notes: 4,
        highlighted: true,
      },
    ],
    collections: [
      { id: 1, name: 'RLHF Core Papers', count: 12, color: 'indigo' },
      { id: 2, name: 'Safety & Alignment', count: 8, color: 'pink' },
      { id: 3, name: 'Evaluation Methods', count: 6, color: 'cyan' },
    ],
  };

  // Calendar Data
  const calendarEvents = [
    { 
      id: 1, 
      title: 'Weekly Sync', 
      time: '10:00 AM', 
      duration: '1h',
      type: 'meeting',
      attendees: ['SC', 'AK', 'PS', 'JL'],
      color: 'blue',
      isToday: true,
    },
    { 
      id: 2, 
      title: 'Paper Discussion: DPO', 
      time: '2:00 PM', 
      duration: '45m',
      type: 'discussion',
      attendees: ['SC', 'PS'],
      color: 'indigo',
      isToday: true,
    },
    { 
      id: 3, 
      title: 'Experiment Review', 
      time: '4:30 PM', 
      duration: '30m',
      type: 'review',
      attendees: ['AK', 'JL'],
      color: 'green',
      isToday: true,
    },
    { 
      id: 4, 
      title: 'Methodology Deadline', 
      time: 'All Day', 
      duration: '',
      type: 'deadline',
      attendees: [],
      color: 'red',
      isToday: false,
      dayOffset: 2,
    },
    { 
      id: 5, 
      title: 'Guest Lecture: AI Safety', 
      time: '11:00 AM', 
      duration: '2h',
      type: 'event',
      attendees: ['SC', 'AK', 'PS', 'JL', '+8'],
      color: 'amber',
      isToday: false,
      dayOffset: 3,
    },
  ];

  const calendarDays = [
    { day: 'Mon', date: 27, isToday: false, hasEvents: true },
    { day: 'Tue', date: 28, isToday: false, hasEvents: false },
    { day: 'Wed', date: 29, isToday: true, hasEvents: true },
    { day: 'Thu', date: 30, isToday: false, hasEvents: true },
    { day: 'Fri', date: 31, isToday: false, hasEvents: true },
    { day: 'Sat', date: 1, isToday: false, hasEvents: false },
    { day: 'Sun', date: 2, isToday: false, hasEvents: false },
  ];

  // Render Discovery View
  const renderDiscoveryView = () => (
    <div className="labs-discovery">
      {/* Header */}
      <header className="labs-header">
        <div className="labs-header-left">
          <div className="labs-title-row">
            <h2 className="labs-page-title">Research Labs</h2>
            <div className="labs-title-badge">
              <Beaker size={10} />
              <span>156 Active</span>
            </div>
          </div>
          <p className="labs-page-subtitle">Join collaborative research teams</p>
        </div>
        <button className="labs-create-btn">
          <Plus size={11} />
          <span>Create Lab</span>
        </button>
      </header>

      {/* Tabs */}
      <div className="labs-tabs-container">
        <div className="labs-tabs">
          <button className="labs-tab active">
            <Sparkles size={10} />
            <span>For You</span>
          </button>
          <button className="labs-tab">
            <TrendingUp size={10} />
            <span>Trending</span>
          </button>
          <button className="labs-tab">
            <Flame size={10} />
            <span>Hot</span>
          </button>
          <button className="labs-tab">
            <Clock size={10} />
            <span>New</span>
          </button>
          <button className="labs-tab">
            <Users size={10} />
            <span>Network</span>
          </button>
        </div>
        <button className="labs-refresh-btn">
          <RefreshCw size={10} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Labs Grid */}
      <div className="labs-grid">
        {labsData.map((lab, index) => (
          <div 
            key={lab.id}
            className={`labs-card labs-card--${lab.color} ${index < visibleCards ? 'visible' : ''}`}
            style={{ transitionDelay: `${index * 100}ms` }}
          >
            {/* Card Image */}
            <div 
              className={`labs-card-image labs-card-image--${lab.image}`}
              style={lab.fieldImage ? { backgroundImage: `url(${process.env.PUBLIC_URL}${lab.fieldImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
            >
              <div className="labs-card-spots">
                {lab.spotsLeft} spots left
              </div>
            </div>

            {/* Card Content */}
            <div className="labs-card-content">
              {/* Tags */}
              <div className="labs-card-tags">
                {lab.tags.map((tag, i) => (
                  <span key={i} className="labs-card-tag">{tag}</span>
                ))}
              </div>

              {/* Title & Description */}
              <h3 className="labs-card-title">{lab.name}</h3>
              <p className="labs-card-desc">{lab.description}</p>

              {/* Stats */}
              <div className="labs-card-stats">
                <span className={`labs-card-match ${lab.match >= 70 ? 'high' : lab.match >= 40 ? 'medium' : 'low'}`}>
                  {lab.match}% Match
                </span>
                <span className="labs-card-quality">
                  <Star size={9} />
                  {lab.quality} Quality
                </span>
              </div>

              {/* Leader */}
              <div className="labs-card-leader">
                <div className="labs-leader-avatar">{lab.leader.avatar}</div>
                <div className="labs-leader-info">
                  <span className="labs-leader-badge">LEADER</span>
                  <span className="labs-leader-name">{lab.leader.name}</span>
                </div>
              </div>

              {/* Footer */}
              <div className="labs-card-footer">
                <div className="labs-card-meta">
                  <span className="labs-meta-item">
                    <Users size={9} />
                    {lab.members}/{lab.maxMembers}
                  </span>
                  <span className="labs-meta-item">
                    <Clock size={9} />
                    {lab.lastActive}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="labs-card-actions">
                <button className="labs-btn labs-btn--secondary">
                  <Eye size={10} />
                  Preview
                </button>
                <button className="labs-btn labs-btn--primary">
                  <UserPlus size={10} />
                  Join
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render Chat View
  const renderChatView = () => (
    <div className="labs-chat">
      {/* Online Members */}
      <div className="labs-chat-members">
        <span className="labs-chat-online">
          <span className="labs-online-dot"></span>
          8 online
        </span>
        <div className="labs-chat-avatars">
          {['SC', 'AK', 'PS', 'JL'].map((avatar, i) => (
            <div key={i} className="labs-chat-avatar" style={{ zIndex: 4 - i }}>
              {avatar}
            </div>
          ))}
          <div className="labs-chat-avatar labs-chat-avatar--more">+4</div>
        </div>
      </div>

      {/* Messages */}
      <div className="labs-chat-messages">
        {chatMessages.map((msg, index) => (
          <div 
            key={msg.id} 
            className={`labs-message ${msg.isMe ? 'labs-message--me' : ''} ${index < visibleTasks ? 'visible' : ''}`}
            style={{ transitionDelay: `${index * 100}ms` }}
          >
            {!msg.isMe && (
              <div className="labs-message-avatar">{msg.user.avatar}</div>
            )}
            <div className="labs-message-content">
              {!msg.isMe && (
                <div className="labs-message-header">
                  <span className="labs-message-name">{msg.user.name}</span>
                  <span className="labs-message-role">{msg.user.role}</span>
                  <span className="labs-message-time">{msg.time}</span>
                </div>
              )}
              <div className="labs-message-bubble">
                <p className="labs-message-text">{msg.message}</p>
                {msg.hasAttachment && (
                  <div className="labs-message-attachment">
                    <FileText size={12} />
                    <span>{msg.attachmentName}</span>
                    <Download size={10} />
                  </div>
                )}
              </div>
              {msg.reactions.length > 0 && (
                <div className="labs-message-reactions">
                  {msg.reactions.map((reaction, i) => (
                    <span key={i} className="labs-reaction">
                      {reaction.emoji} {reaction.count}
                    </span>
                  ))}
                </div>
              )}
              {msg.isMe && (
                <span className="labs-message-time labs-message-time--me">{msg.time}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Chat Input */}
      <div className="labs-chat-input">
        <button className="labs-chat-btn">
          <Paperclip size={12} />
        </button>
        <input 
          type="text" 
          className="labs-chat-field" 
          placeholder="Type a message..."
        />
        <button className="labs-chat-btn">
          <Smile size={12} />
        </button>
        <button className="labs-chat-send">
          <Send size={12} />
        </button>
      </div>
    </div>
  );

  // Render Tasks View
  const renderTasksView = () => (
    <div className="labs-tasks">
      {/* Tasks Header */}
      <div className="labs-tasks-header">
        <div className="labs-tasks-title-row">
          <h3 className="labs-tasks-title">Tasks</h3>
          <span className="labs-tasks-count">8 / 12</span>
        </div>
        <div className="labs-tasks-actions">
          <button className="labs-view-btn active">
            <LayoutGrid size={10} />
            Board
          </button>
          <button className="labs-view-btn">
            <BarChart3 size={10} />
            Stats
          </button>
          <button className="labs-add-task-btn">
            <Plus size={10} />
            Add Task
          </button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="labs-kanban">
        {taskColumns.map((column, colIndex) => (
          <div 
            key={column.id} 
            className={`labs-kanban-column labs-kanban-column--${column.color} ${colIndex < visibleTasks ? 'visible' : ''}`}
            style={{ transitionDelay: `${colIndex * 80}ms` }}
          >
            <div className="labs-kanban-header">
              <column.icon size={12} className="labs-kanban-icon" />
              <span className="labs-kanban-count">
                {tasks.filter(t => t.column === column.id).length}
              </span>
              <span className="labs-kanban-name">{column.name}</span>
            </div>
            <div className="labs-kanban-tasks">
              {tasks
                .filter(task => task.column === column.id)
                .slice(0, 2)
                .map((task, taskIndex) => (
                  <div 
                    key={task.id} 
                    className={`labs-task-card labs-task-card--${task.priority}`}
                  >
                    <div className="labs-task-title">{task.title}</div>
                    <div className="labs-task-footer">
                      <div className="labs-task-assignee">{task.assignee}</div>
                      <div className="labs-task-due">{task.dueDate}</div>
                    </div>
                  </div>
                ))}
              {tasks.filter(t => t.column === column.id).length > 2 && (
                <div className="labs-task-more">
                  +{tasks.filter(t => t.column === column.id).length - 2} more
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render Files View
  const renderFilesView = () => (
    <div className="labs-files">
      {/* Files Header */}
      <div className="labs-files-header">
        <div className="labs-files-title-row">
          <h3 className="labs-files-title">Files & Resources</h3>
          <span className="labs-files-count">54 files • 2.8 GB</span>
        </div>
        <div className="labs-files-actions">
          <div className="labs-files-search">
            <Search size={10} />
            <input type="text" placeholder="Search files..." />
          </div>
          <button className="labs-upload-btn">
            <FilePlus size={10} />
            Upload
          </button>
        </div>
      </div>

      {/* Folders */}
      <div className="labs-folders">
        <h4 className="labs-section-label">Folders</h4>
        <div className="labs-folders-grid">
          {filesData.folders.map((folder, index) => (
            <div 
              key={folder.id} 
              className={`labs-folder labs-folder--${folder.color} ${index < visibleTasks ? 'visible' : ''}`}
              style={{ transitionDelay: `${index * 80}ms` }}
            >
              <Folder size={16} className="labs-folder-icon" />
              <div className="labs-folder-info">
                <span className="labs-folder-name">{folder.name}</span>
                <span className="labs-folder-meta">{folder.files} files • {folder.updated}</span>
              </div>
              <ChevronRight size={12} className="labs-folder-arrow" />
            </div>
          ))}
        </div>
      </div>

      {/* Recent Files */}
      <div className="labs-recent-files">
        <h4 className="labs-section-label">Recent Files</h4>
        <div className="labs-files-list">
          {filesData.recentFiles.map((file, index) => (
            <div 
              key={file.id} 
              className={`labs-file-item ${index < visibleTasks ? 'visible' : ''}`}
              style={{ transitionDelay: `${(index + 3) * 60}ms` }}
            >
              <div className={`labs-file-icon labs-file-icon--${file.type}`}>
                {file.type === 'code' && <FileCode size={12} />}
                {file.type === 'doc' && <FileText size={12} />}
                {file.type === 'image' && <FileImage size={12} />}
                {file.type === 'pdf' && <File size={12} />}
              </div>
              <div className="labs-file-info">
                <span className="labs-file-name">{file.name}</span>
                <span className="labs-file-meta">{file.size} • {file.updated} • {file.author}</span>
              </div>
              <div className="labs-file-actions">
                <button className="labs-file-btn"><Download size={10} /></button>
                <button className="labs-file-btn"><Share2 size={10} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Render Library View
  const renderLibraryView = () => (
    <div className="labs-library">
      {/* Library Header */}
      <div className="labs-library-header">
        <div className="labs-library-title-row">
          <h3 className="labs-library-title">Research Library</h3>
          <span className="labs-library-count">26 papers</span>
        </div>
        <div className="labs-library-actions">
          <button className="labs-add-paper-btn">
            <Plus size={10} />
            Add Paper
          </button>
        </div>
      </div>

      {/* Collections */}
      <div className="labs-collections">
        <h4 className="labs-section-label">Collections</h4>
        <div className="labs-collections-row">
          {libraryData.collections.map((col, index) => (
            <div 
              key={col.id} 
              className={`labs-collection labs-collection--${col.color} ${index < visibleTasks ? 'visible' : ''}`}
              style={{ transitionDelay: `${index * 80}ms` }}
            >
              <BookOpen size={12} />
              <span className="labs-collection-name">{col.name}</span>
              <span className="labs-collection-count">{col.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Papers */}
      <div className="labs-papers">
        <h4 className="labs-section-label">Recent Papers</h4>
        <div className="labs-papers-list">
          {libraryData.papers.map((paper, index) => (
            <div 
              key={paper.id} 
              className={`labs-paper-item ${paper.highlighted ? 'labs-paper-item--highlighted' : ''} ${index < visibleTasks ? 'visible' : ''}`}
              style={{ transitionDelay: `${(index + 3) * 60}ms` }}
            >
              <div className="labs-paper-main">
                <h4 className="labs-paper-title">{paper.title}</h4>
                <div className="labs-paper-meta">
                  <span className="labs-paper-authors">{paper.authors}</span>
                  <span className="labs-paper-divider">•</span>
                  <span className="labs-paper-year">{paper.year}</span>
                  <span className="labs-paper-divider">•</span>
                  <span className="labs-paper-journal">{paper.journal}</span>
                </div>
                <div className="labs-paper-stats">
                  <span className="labs-paper-citations">
                    <BarChart3 size={9} />
                    {paper.citations} citations
                  </span>
                  <span className="labs-paper-notes">
                    <MessageSquare size={9} />
                    {paper.notes} notes
                  </span>
                  <span className="labs-paper-added">
                    Added by {paper.addedBy}
                  </span>
                </div>
              </div>
              <div className="labs-paper-actions">
                <button className="labs-paper-btn"><ExternalLink size={10} /></button>
                <button className="labs-paper-btn"><Heart size={10} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Render Calendar View
  const renderCalendarView = () => (
    <div className="labs-calendar">
      {/* Calendar Header */}
      <div className="labs-calendar-header">
        <div className="labs-calendar-nav">
          <button className="labs-calendar-nav-btn"><ChevronLeft size={12} /></button>
          <h3 className="labs-calendar-month">January 2026</h3>
          <button className="labs-calendar-nav-btn"><ChevronRight size={12} /></button>
        </div>
        <div className="labs-calendar-actions">
          <button className="labs-add-event-btn">
            <Plus size={10} />
            New Event
          </button>
        </div>
      </div>

      {/* Week View */}
      <div className="labs-week-view">
        {calendarDays.map((day, index) => (
          <div 
            key={index} 
            className={`labs-day-column ${day.isToday ? 'labs-day-column--today' : ''} ${index < visibleTasks ? 'visible' : ''}`}
            style={{ transitionDelay: `${index * 60}ms` }}
          >
            <div className="labs-day-header">
              <span className="labs-day-name">{day.day}</span>
              <span className={`labs-day-date ${day.isToday ? 'labs-day-date--today' : ''}`}>
                {day.date}
              </span>
            </div>
            {day.hasEvents && (
              <div className="labs-day-dot"></div>
            )}
          </div>
        ))}
      </div>

      {/* Today's Events */}
      <div className="labs-events">
        <h4 className="labs-section-label">Today's Schedule</h4>
        <div className="labs-events-list">
          {calendarEvents.filter(e => e.isToday).map((event, index) => (
            <div 
              key={event.id} 
              className={`labs-event labs-event--${event.color} ${index < visibleTasks ? 'visible' : ''}`}
              style={{ transitionDelay: `${(index + 7) * 60}ms` }}
            >
              <div className="labs-event-time">
                <span className="labs-event-hour">{event.time}</span>
                {event.duration && <span className="labs-event-duration">{event.duration}</span>}
              </div>
              <div className="labs-event-content">
                <span className="labs-event-title">{event.title}</span>
                <div className="labs-event-attendees">
                  {event.attendees.slice(0, 3).map((att, i) => (
                    <div key={i} className="labs-event-avatar">{att}</div>
                  ))}
                  {event.attendees.length > 3 && (
                    <span className="labs-event-more">+{event.attendees.length - 3}</span>
                  )}
                </div>
              </div>
              <div className="labs-event-actions">
                {event.type === 'meeting' && <Video size={10} />}
                {event.type === 'discussion' && <MessageCircle size={10} />}
                {event.type === 'review' && <Eye size={10} />}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Upcoming */}
      <div className="labs-upcoming">
        <h4 className="labs-section-label">Upcoming</h4>
        <div className="labs-upcoming-list">
          {calendarEvents.filter(e => !e.isToday).map((event, index) => (
            <div 
              key={event.id} 
              className={`labs-upcoming-item labs-upcoming-item--${event.color}`}
            >
              <div className="labs-upcoming-date">
                <span>+{event.dayOffset}d</span>
              </div>
              <div className="labs-upcoming-info">
                <span className="labs-upcoming-title">{event.title}</span>
                <span className="labs-upcoming-time">{event.time}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Render Interior View (Inside a Lab)
  const renderInteriorView = () => (
    <div className="labs-interior">
      {/* Interior Header */}
      <header className="labs-interior-header">
        <div className="labs-interior-left">
          <button className="labs-back-btn">
            <ArrowLeft size={12} />
          </button>
          <h2 className="labs-interior-title">AI Safety Research</h2>
        </div>
        <div className="labs-interior-tabs">
          <button 
            className={`labs-interior-tab ${activeInteriorTab === 'chat' ? 'active' : ''}`}
            onClick={() => onTabClick('chat')}
          >
            <MessageCircle size={11} />
            <span>Chat</span>
          </button>
          <button 
            className={`labs-interior-tab ${activeInteriorTab === 'tasks' ? 'active' : ''}`}
            onClick={() => onTabClick('tasks')}
          >
            <CheckSquare size={11} />
            <span>Tasks</span>
          </button>
          <button 
            className={`labs-interior-tab ${activeInteriorTab === 'files' ? 'active' : ''}`}
            onClick={() => onTabClick('files')}
          >
            <FolderOpen size={11} />
            <span>Files</span>
          </button>
          <button 
            className={`labs-interior-tab ${activeInteriorTab === 'library' ? 'active' : ''}`}
            onClick={() => onTabClick('library')}
          >
            <BookOpen size={11} />
            <span>Library</span>
          </button>
          <button 
            className={`labs-interior-tab ${activeInteriorTab === 'calendar' ? 'active' : ''}`}
            onClick={() => onTabClick('calendar')}
          >
            <Calendar size={11} />
            <span>Calendar</span>
          </button>
        </div>
        <div className="labs-interior-right">
          <div className="labs-interior-members">
            <Users size={11} />
            <span>12</span>
          </div>
          <button className="labs-interior-settings">
            <Settings size={12} />
          </button>
        </div>
      </header>

      {/* Tab Content */}
      <div className="labs-interior-content">
        {activeInteriorTab === 'chat' && renderChatView()}
        {activeInteriorTab === 'tasks' && renderTasksView()}
        {activeInteriorTab === 'files' && renderFilesView()}
        {activeInteriorTab === 'library' && renderLibraryView()}
        {activeInteriorTab === 'calendar' && renderCalendarView()}
      </div>
    </div>
  );

  return (
    <div className="labs-mockup-content">
      {showInterior ? renderInteriorView() : renderDiscoveryView()}
    </div>
  );
};

export default LabsMockupContent;
